import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { VendorService } from 'src/app/services/core/quickbook/vendors/vendor.service';
import { CompanyService } from 'src/app/services/core/company/company.service';
import { sweetAlert } from 'src/app/utils/swal';
import { allowOnlyNumbersValidator, phoneNumberLengthValidator } from 'src/app/utils/utils.common';
import { addressValidator } from 'src/app/utils/custom-validators';

@UntilDestroy()

@Component({
  selector: 'app-add-vendor',
  templateUrl: './add-vendor.component.html',
  styleUrls: ['./add-vendor.component.css']
})
export class AddVendorComponent implements OnInit {
  @Input() vendor: any;
  @Input() source: any;
  @Output() onSaveVendor = new EventEmitter<string>();
  @Output() onCloseModal = new EventEmitter<string>();

  vendorForm!: FormGroup

  vendorId: any;
  quickBookId: any;
  selectedCompany: any = '';
  companies: any;
  submitted = false;
  syncToken: any;
  vendorData: any;
  private suppressCompanyChange = false;

  constructor(
    private companyService: CompanyService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private vendorService: VendorService,
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.getCompanies();
    this.buildForm();

    if (this.source === 'modal' && this.vendor) {
      this.vendorId = this.vendor.id;
      this.getVendorById();
    } else {
      this.activatedRoute.params
        .pipe(untilDestroyed(this)).subscribe((params) => {
          this.vendorId = params["id"];
          if (this.vendorId) {
            this.getVendorById();
          }
        }
      );
    }
  }

  get form() {
    return this.vendorForm.controls;
  }

  get primaryEmailAddr() {
    return this.vendorForm.get('PrimaryEmailAddr') as FormGroup;
  }

  get primaryPhone() {
    return this.vendorForm.get('PrimaryPhone') as FormGroup;
  }

  get billAddr() {
    return this.vendorForm.get('BillAddr') as FormGroup;
  }

  get reference() {
    return this.vendorForm.get('reference') as FormGroup;
  }

  buildForm() {
    this.vendorForm = this.fb.group({
      PrimaryEmailAddr: this.fb.group({
        Address: ['', Validators.required],
      }),
      DisplayName: ['', Validators.required],
      Notes: [''],
      PrimaryPhone: this.fb.group({
        FreeFormNumber: ['', [Validators.required, phoneNumberLengthValidator]],
      }, { validators: allowOnlyNumbersValidator('FreeFormNumber') }),
      BillAddr: this.fb.group({
        Line1: ['', Validators.required],
        CountrySubDivisionCode: ['', Validators.required],
        City: ['', Validators.required],
        PostalCode: ['', Validators.required],
        Country: ['', Validators.required],
        coords: [[]],
      }, { validators: addressValidator('Line1') }),
      reference: this.fb.group({
        name: [''],
        id: [''],
      })
    });
  }

  getVendorById() {
    this.vendorService.getVendorById(this.vendorId).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.vendorData = data;
        this.syncToken = data.SyncToken;
        this.quickBookId = data.Id;
        this.patchVendorForm();
      }
    })
  }

  patchVendorForm() {
    const data = this.vendorData;
    if (!data) return;
    this.suppressCompanyChange = true;
    this.vendorForm.patchValue(data);
    this.selectedCompany = data.reference?.id || '';
    setTimeout(() => {
      this.suppressCompanyChange = false;
    });
  }

  onSave() {
    this.submitted = true;
    if (this.vendorForm.invalid) {
      return;
    }
    const payload = {
      ...this.vendorForm.value,
      FullyQualifiedName: this.vendorForm.value.DisplayName,
    }

    if (this.vendorId) {
      payload.SyncToken = this.syncToken,
        payload.Id = this.quickBookId
    }
    if (payload?.BillAddr) {
      delete payload.BillAddr.coords;
    }

    this.vendorService.saveVendor(this.vendorId, payload).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        if (this.source === 'modal') {
          this.onSaveVendor.emit(data);
        } else {
          this.router.navigateByUrl('/quickbook/vendors')
        }
      }
    }, (err) => {
        sweetAlert("Error", err, "error", "Ok");
      })
  }

  getCompanies() {
    const queryparams: any = {
      limit: 9999,
      isActive: true
    }
    this.companyService
      .getPickerCompanies(queryparams)
      .pipe(untilDestroyed(this)).subscribe((data: any) => {
        if (data && data.results) {
          this.companies = data.results.sort((a: any, b: any) =>
            (a.name).toLowerCase().localeCompare((b.name).toLowerCase())
          );
          this.patchVendorForm();
        }
      });
  }

  onSelectAddress(place: any): void {
    const selectedAddress = place.address_components;
    const city = selectedAddress.find((data: any) =>
      data.types.includes('locality') || data.types.includes('administrative_area_level_2')
    );
    const cityName = city ? city.long_name : '';

    const state = selectedAddress.find((data: any) =>
      data.types.includes('administrative_area_level_1')
    );
    const stateName = state ? state.short_name : '';

    const postalCode = selectedAddress.find((data: any) =>
      data.types.includes('postal_code')
    );
    const postalCodeValue = postalCode ? postalCode.long_name : '';

    const country = selectedAddress.find((data: any) =>
      data.types.includes('country')
    );
    const countryName = country ? country.long_name : '';

    this.vendorForm.patchValue({
      BillAddr: {
        Line1: place.formatted_address,
        CountrySubDivisionCode: stateName,
        City: cityName,
        PostalCode: postalCodeValue,
        Country: countryName,
        coords: [
          place.geometry.location.lng(),
          place.geometry.location.lat()
        ]
      }
    });
  }

  onCompanyChange() {
    if (this.suppressCompanyChange) return;
    const company = this.companies?.find((item: any) => String(item.id) === String(this.selectedCompany));
    if (!company) return;
    this.vendorForm.get('reference')?.patchValue({
        name: company.name,
        id: company.id
    })
  }

  onChangeAddress() {
    this.vendorForm.patchValue({
      BillAddr: {
        City: '',
        CountrySubDivisionCode: '',
        PostalCode: '',
        Country: '',
        coords: ''
      }
    });
  }
}
